import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo, ReactNode } from 'react';
import { getIntakePrefillData } from '@/services/intakeService';
import { mapPrefillToPatientIntake } from '@/lib/intake/prefillMapper';

// Types for intake form state
export interface BasicInfo {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  date_of_birth: string;
  biological_sex: 'male' | 'female' | 'unknown' | '';
  city: string;
  state: string;
  zip_code: string;
}

export interface MedicalHistory {
  conditions: string[];
  medications: string[];
  allergies: string[];
  surgeries: string[];
  family_history: string[];
}

export interface QuestionnaireResponses {
  adam: Record<string, boolean>;
  stopBang: Record<string, boolean>;
  symptoms: Record<string, number | string>;
}

export interface LifestyleInfo {
  exercise_frequency: string;
  diet_quality: string;
  sleep_hours: number;
  alcohol_frequency: string;
  smoking_status: string;
  stress_level: number;
  goals: string[];
  /** Inches. Persisted to fhir_observations via vitalsService on intake submit. */
  height: string;
  /** Pounds. Persisted to fhir_observations via vitalsService on intake submit. */
  weight: string;
}

export interface ShippingBillingInfo {
  shippingAddressLine1: string;
  shippingAddressLine2: string;
  shippingCity: string;
  shippingState: string;
  shippingPostalCode: string;
  billingAddressLine1: string;
  billingAddressLine2: string;
  billingCity: string;
  billingState: string;
  billingPostalCode: string;
  billingSameAsShipping: boolean;
}

export interface IntakeFormState {
  token: string | null;
  currentStep: number;
  therapyCategory: string;
  basic_info: BasicInfo;
  medical_history: MedicalHistory;
  questionnaires: QuestionnaireResponses;
  lifestyle: LifestyleInfo;
  shipping_billing: ShippingBillingInfo;
  provider_name: string;
  clinic_name: string;
  expires_at: string;
  intake_type: 'targeted' | 'full';
  missing_fields: string[];
  // Auto-save related fields
  hasDraft: boolean;
  lastSavedAt: string | null;
  isSaving: boolean;
  saveError: string | null;
  isGuided: boolean;
  paymentStatus: string | null;
  /** True for Nimbus-fulfilled guided orders — enables the inline booking step. */
  isNimbusBookingEligible: boolean;
  /** Slot picked in the booking step; included with the bundled submit. */
  selectedSlot: { slot_start: string; slot_end: string; timezone: string } | null;
}

// Targeted mode step navigation helpers
export interface TargetedNavigation {
  /** Ordered list of active step numbers (e.g. [1, 3, 5]) */
  activeSteps: number[];
  /** Whether the form is in targeted mode */
  isTargeted: boolean;
  /** Total number of active steps */
  totalActiveSteps: number;
  /** 1-based position of a step within activeSteps (e.g. step 3 might be position 2) */
  stepPosition: (step: number) => number;
  /** Next step number from current, or null if at end */
  nextStep: (current: number) => number | null;
  /** Previous step number from current, or null if at start */
  prevStep: (current: number) => number | null;
  /** Step configs for IntakeProgress */
  activeStepConfigs: { number: number; title: string; estimatedMinutes: number; isRequired: boolean }[];
}

/** Map missing_fields categories to step numbers */
function computeActiveSteps(
  intakeType: string,
  missingFields: string[],
  showPayment: boolean,
  showBooking: boolean,
): number[] {
  // Order: base steps → BOOKING (8) → PAYMENT (7). Booking is inserted before
  // payment for the bundled inline-booking flow.
  const appendOptional = (steps: number[]) => {
    if (showBooking) steps.push(8);
    if (showPayment) steps.push(7);
    return steps;
  };

  if (intakeType !== 'targeted' || !missingFields || missingFields.length === 0) {
    return appendOptional([1, 2, 3, 4, 5, 6]);
  }

  const categoryToStep: Record<string, number> = {
    demographics: 1,
    basic_info: 1,
    medical_history: 2,
    symptoms: 3,
    questionnaire: 3,
    vitals: 4,
    lifestyle: 4,
    shipping_billing: 5,
    address: 5,
  };

  const neededSteps = new Set<number>();
  for (const fieldId of missingFields) {
    // Check if the field ID itself maps to a category
    const directStep = categoryToStep[fieldId];
    if (directStep) {
      neededSteps.add(directStep);
      continue;
    }
    // Fall back: try matching the field ID as a known field
    if (['first_name', 'last_name', 'dob', 'date_of_birth', 'sex_at_birth', 'biological_sex', 'phone', 'email'].includes(fieldId)) {
      neededSteps.add(1);
    } else if (['medical_history', 'past_surgeries', 'allergies', 'current_medications', 'conditions', 'medications', 'family_history'].includes(fieldId)) {
      neededSteps.add(2);
    } else if (['symptoms_assessment', 'stop_bang', 'adam', 'symptoms'].includes(fieldId)) {
      neededSteps.add(3);
    } else if (['height', 'weight', 'blood_pressure', 'lifestyle', 'exercise_frequency', 'diet_quality', 'sleep_hours', 'stress_level'].includes(fieldId)) {
      neededSteps.add(4);
    } else if (['shipping_address', 'billing_address', 'address', 'shippingAddress', 'billingAddress'].includes(fieldId)) {
      neededSteps.add(5);
    } else {
      // Unknown field -- include its nearest category step (default to step 2 for safety)
      neededSteps.add(2);
    }
  }

  // Always include Step 5 (Address) and Step 6 (Review)
  neededSteps.add(5);
  neededSteps.add(6);

  const ordered = Array.from(neededSteps).sort((a, b) => a - b);
  return appendOptional(ordered);
}

const ALL_STEP_CONFIGS = [
  { number: 1, title: 'Basic Info', estimatedMinutes: 2, isRequired: true },
  { number: 2, title: 'Health History', estimatedMinutes: 3, isRequired: true },
  { number: 3, title: 'Questionnaires', estimatedMinutes: 5, isRequired: true },
  { number: 4, title: 'Lifestyle', estimatedMinutes: 2, isRequired: false },
  { number: 5, title: 'Address', estimatedMinutes: 2, isRequired: true },
  { number: 6, title: 'Review & Submit', estimatedMinutes: 1, isRequired: true },
  { number: 7, title: 'Payment', estimatedMinutes: 1, isRequired: true },
  { number: 8, title: 'Book Appointment', estimatedMinutes: 2, isRequired: true },
];

interface IntakeContextType {
  formState: IntakeFormState;
  updateFormState: (updates: Partial<IntakeFormState> | ((prev: IntakeFormState) => Partial<IntakeFormState>)) => void;
  resetForm: () => void;
  validateToken: (token: string) => Promise<{ success: boolean; error?: string; hasDraft?: boolean; data?: { providerName: string; clinicName?: string | null; expiresAt: string; therapyCategory: string; intakeType: string; missingFields: string[]; hasDraft?: boolean; draftData?: any; currentStep?: number } }>;
  submitIntake: (payment?: { paymentMethodId: string; setupIntentId: string }) => Promise<{
    success: boolean;
    error?: string;
    isCancelled?: boolean;
    appointment?: {
      id: string;
      start_time: string;
      end_time: string;
      duration: number;
      telehealthLink?: string | null;
    } | null;
  }>;
  saveDraft: () => Promise<{ success: boolean; error?: string }>;
  loadDraft: () => void;
  clearDraft: () => void;
  targetedNav: TargetedNavigation;
}

const initialState: IntakeFormState = {
  token: null,
  currentStep: 1,
  therapyCategory: 'general',
  basic_info: {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    biological_sex: '',
    city: '',
    state: '',
    zip_code: '',
  },
  medical_history: {
    conditions: [],
    medications: [],
    allergies: [],
    surgeries: [],
    family_history: [],
  },
  questionnaires: {
    adam: {},
    stopBang: {},
    symptoms: {},
  },
  lifestyle: {
    exercise_frequency: '',
    diet_quality: '',
    sleep_hours: 7,
    alcohol_frequency: '',
    smoking_status: '',
    stress_level: 5,
    goals: [],
    height: '',
    weight: '',
  },
  shipping_billing: {
    shippingAddressLine1: '',
    shippingAddressLine2: '',
    shippingCity: '',
    shippingState: '',
    shippingPostalCode: '',
    billingAddressLine1: '',
    billingAddressLine2: '',
    billingCity: '',
    billingState: '',
    billingPostalCode: '',
    billingSameAsShipping: true,
  },
  provider_name: '',
  clinic_name: '',
  expires_at: '',
  intake_type: 'full',
  missing_fields: [],
  // Auto-save fields
  hasDraft: false,
  lastSavedAt: null,
  isSaving: false,
  saveError: null,
  isGuided: false,
  paymentStatus: null,
  isNimbusBookingEligible: false,
  selectedSlot: null,
};

// Auto-save interval in milliseconds (30 seconds)
const AUTO_SAVE_INTERVAL = 30000;

const IntakeContext = createContext<IntakeContextType | undefined>(undefined);

export const IntakeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [formState, setFormState] = useState<IntakeFormState>(() => {
    // Try to restore from sessionStorage
    const saved = sessionStorage.getItem('novamed_intake_form');
    if (saved) {
      try {
        return { ...initialState, ...JSON.parse(saved) };
      } catch {
        return initialState;
      }
    }
    return initialState;
  });

  // Ref for auto-save interval
  const autoSaveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // Ref to track if form has been modified since last save
  const formModifiedRef = useRef(false);

  const updateFormState = useCallback((updates: Partial<IntakeFormState> | ((prev: IntakeFormState) => Partial<IntakeFormState>)) => {
    setFormState(prev => {
      const resolved = typeof updates === 'function' ? updates(prev) : updates;
      const newState = { ...prev, ...resolved };
      // Persist to sessionStorage
      sessionStorage.setItem('novamed_intake_form', JSON.stringify(newState));
      // Mark form as modified for auto-save
      formModifiedRef.current = true;
      return newState;
    });
  }, []);

  const resetForm = useCallback(() => {
    setFormState(initialState);
    sessionStorage.removeItem('novamed_intake_form');
    formModifiedRef.current = false;
  }, []);

  // Save draft to backend
  const saveDraft = useCallback(async () => {
    if (!formState.token) {
      return { success: false, error: 'No token available' };
    }

    // Don't save if nothing has changed
    if (!formModifiedRef.current) {
      return { success: true };
    }

    // Skip auto-save once the intake has been submitted — the backend will
    // 400 with "intake already completed" and clutter logs on every tick.
    if (sessionStorage.getItem(`patient-intake-submitted-${formState.token}`) === 'true') {
      formModifiedRef.current = false;
      return { success: true };
    }

    setFormState(prev => ({ ...prev, isSaving: true, saveError: null }));

    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const draftData = {
        basic_info: formState.basic_info,
        medical_history: formState.medical_history,
        questionnaires: formState.questionnaires,
        lifestyle: formState.lifestyle,
        shipping_billing: formState.shipping_billing,
      };

      const response = await fetch(`${baseUrl}intakes/draft/${formState.token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draftData,
          currentStep: formState.currentStep,
        }),
      });

      const data = await response.json();

      if (data.success) {
        formModifiedRef.current = false;
        setFormState(prev => ({
          ...prev,
          isSaving: false,
          lastSavedAt: data.data?.savedAt || new Date().toISOString(),
          hasDraft: true,
          saveError: null,
        }));
        return { success: true };
      } else {
        setFormState(prev => ({
          ...prev,
          isSaving: false,
          saveError: data.message || 'Failed to save draft',
        }));
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error('Draft save error:', error);
      // For demo tokens, just update local state
      if (formState.token?.startsWith('demo')) {
        formModifiedRef.current = false;
        setFormState(prev => ({
          ...prev,
          isSaving: false,
          lastSavedAt: new Date().toISOString(),
          hasDraft: true,
        }));
        return { success: true };
      }
      setFormState(prev => ({
        ...prev,
        isSaving: false,
        saveError: 'Network error while saving',
      }));
      return { success: false, error: 'Network error while saving' };
    }
  }, [formState.token, formState.basic_info, formState.medical_history, formState.questionnaires, formState.lifestyle, formState.currentStep]);

  // Load draft from state (called after token validation)
  const loadDraft = useCallback(() => {
    const saved = sessionStorage.getItem('novamed_intake_form');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.hasDraft && parsed.token === formState.token) {
          // Draft already loaded from sessionStorage
          return;
        }
      } catch {
        // Ignore parsing errors
      }
    }
  }, [formState.token]);

  // Clear draft
  const clearDraft = useCallback(() => {
    setFormState(prev => ({
      ...prev,
      hasDraft: false,
      lastSavedAt: null,
    }));
    formModifiedRef.current = false;
  }, []);

  // Auto-save effect
  useEffect(() => {
    // Only start auto-save if we have a token and are on step > 0
    if (formState.token && formState.currentStep > 0) {
      // Clear any existing interval
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
      }

      // Set up new interval
      autoSaveIntervalRef.current = setInterval(() => {
        if (formModifiedRef.current) {
          saveDraft();
        }
      }, AUTO_SAVE_INTERVAL);

      // Clean up on unmount or when token changes
      return () => {
        if (autoSaveIntervalRef.current) {
          clearInterval(autoSaveIntervalRef.current);
        }
      };
    }
  }, [formState.token, formState.currentStep, saveDraft]);

  // Save draft when user leaves page
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (formModifiedRef.current && formState.token) {
        // Use sendBeacon for reliable save on page exit
        const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
        const draftData = {
          basic_info: formState.basic_info,
          medical_history: formState.medical_history,
          questionnaires: formState.questionnaires,
          lifestyle: formState.lifestyle,
        };
        navigator.sendBeacon(
          `${baseUrl}intakes/draft/${formState.token}`,
          JSON.stringify({ draftData, currentStep: formState.currentStep })
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formState]);

  const validateToken = useCallback(async (token: string) => {
    // Demo token fallback - check first for quick demo access
    const isDemoToken = token === 'demo-token' || token.startsWith('demo-');
    
    const createDemoData = () => ({
      providerName: 'Dr. Demo Provider',
      clinicName: 'Demo Clinic',
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      therapyCategory: 'trt',
      intakeType: 'full' as const,
      missingFields: [],
      hasDraft: false,
      draftData: null,
      currentStep: 1,
    });

    try {
      // Base URL already includes /api from env variable
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
      const response = await fetch(`${baseUrl}/intakes/validate/${token}`);
      
      // If network succeeded but API returned error status
      if (!response.ok) {
        if (isDemoToken) {
          const demoData = createDemoData();
          updateFormState({
            token,
            provider_name: demoData.providerName,
            clinic_name: demoData.clinicName,
            expires_at: demoData.expiresAt,
            therapyCategory: demoData.therapyCategory,
            intake_type: demoData.intakeType,
            missing_fields: demoData.missingFields,
            hasDraft: false,
          });
          return { success: true, data: demoData, hasDraft: false };
        }
        return { success: false, error: 'Invalid or expired token' };
      }

      const data = await response.json();
      
      if (data.success) {
        const responseData = data.data;
        const hasDraft = responseData.hasDraft || false;
        const clinicName = responseData.clinicName || responseData?.clinic?.name || '';
        
        // Fetch prefill data
        let prefillData = {};
        try {
          const prefillResult = await getIntakePrefillData(token);
          if (prefillResult.success && prefillResult.data) {
            prefillData = mapPrefillToPatientIntake(prefillResult.data);
          }
        } catch (error) {
          console.error('Error fetching prefill data:', error);
          // Continue without prefill data
        }
        
        // If there's draft data, restore it (draft takes priority over prefill)
        // Build shipping_billing from prefill address fields
        const prefillShippingBilling: Partial<ShippingBillingInfo> = {};
        const pd = prefillData as Record<string, unknown>;
        // Billing address fields
        if (pd.billingAddressLine1) prefillShippingBilling.billingAddressLine1 = pd.billingAddressLine1 as string;
        if (pd.billingAddressLine2) prefillShippingBilling.billingAddressLine2 = pd.billingAddressLine2 as string;
        if (pd.billingCity) prefillShippingBilling.billingCity = pd.billingCity as string;
        if (pd.billingState) prefillShippingBilling.billingState = pd.billingState as string;
        if (pd.billingPostalCode) prefillShippingBilling.billingPostalCode = pd.billingPostalCode as string;
        // Shipping address fields
        if (pd.shippingAddressLine1) prefillShippingBilling.shippingAddressLine1 = pd.shippingAddressLine1 as string;
        if (pd.shippingAddressLine2) prefillShippingBilling.shippingAddressLine2 = pd.shippingAddressLine2 as string;
        if (pd.shippingCity) prefillShippingBilling.shippingCity = pd.shippingCity as string;
        if (pd.shippingState) prefillShippingBilling.shippingState = pd.shippingState as string;
        if (pd.shippingPostalCode) prefillShippingBilling.shippingPostalCode = pd.shippingPostalCode as string;
        // Toggle state
        if (pd.billingSameAsShipping !== undefined) prefillShippingBilling.billingSameAsShipping = pd.billingSameAsShipping as boolean;

        // Route prefill fields into the correct nested section.
        const prefillMedicalHistory: Partial<typeof initialState.medical_history> = {};
        if (Array.isArray(pd.conditions)) prefillMedicalHistory.conditions = pd.conditions as string[];
        if (Array.isArray(pd.allergies)) prefillMedicalHistory.allergies = pd.allergies as string[];
        if (typeof pd.other === 'string') prefillMedicalHistory.other = pd.other;

        if (hasDraft && responseData.draftData) {
          updateFormState({
            token,
            provider_name: responseData.providerName,
            clinic_name: clinicName,
            expires_at: responseData.expiresAt,
            therapyCategory: responseData.therapyCategory || 'general',
            intake_type: responseData.intakeType || 'full',
            missing_fields: responseData.missingFields || [],
            isGuided: Boolean(responseData.isGuided),
            paymentStatus: responseData.paymentStatus ?? null,
            isNimbusBookingEligible: Boolean(responseData.isNimbusBookingEligible),
            hasDraft: true,
            lastSavedAt: responseData.lastSavedAt,
            currentStep: responseData.currentStep || 1,
            // Merge prefill data with draft data (draft takes priority)
            basic_info: {
              ...initialState.basic_info,
              ...prefillData,
              ...(responseData.draftData.basic_info || {}),
            },
            medical_history: {
              ...initialState.medical_history,
              ...prefillMedicalHistory,
              ...(responseData.draftData.medical_history || {}),
            },
            questionnaires: responseData.draftData.questionnaires || initialState.questionnaires,
            lifestyle: {
              ...initialState.lifestyle,
              ...(responseData.draftData.lifestyle || {}),
            },
            shipping_billing: {
              ...initialState.shipping_billing,
              ...prefillShippingBilling,
              ...(responseData.draftData.shipping_billing || {}),
            },
          });
        } else {
          // No draft - use prefill data as base
          updateFormState({
            token,
            provider_name: responseData.providerName,
            clinic_name: clinicName,
            expires_at: responseData.expiresAt,
            therapyCategory: responseData.therapyCategory || 'general',
            intake_type: responseData.intakeType || 'full',
            missing_fields: responseData.missingFields || [],
            isGuided: Boolean(responseData.isGuided),
            paymentStatus: responseData.paymentStatus ?? null,
            isNimbusBookingEligible: Boolean(responseData.isNimbusBookingEligible),
            hasDraft: false,
            // Apply prefill data to basic_info
            basic_info: {
              ...initialState.basic_info,
              ...prefillData,
            },
            medical_history: {
              ...initialState.medical_history,
              ...prefillMedicalHistory,
            },
            shipping_billing: {
              ...initialState.shipping_billing,
              ...prefillShippingBilling,
            },
          });
        }
        
        return { success: true, data: responseData, hasDraft };
      } else {
        // API returned success: false - use demo fallback if demo token
        if (isDemoToken) {
          const demoData = createDemoData();
          updateFormState({
            token,
            provider_name: demoData.providerName,
            clinic_name: demoData.clinicName,
            expires_at: demoData.expiresAt,
            therapyCategory: demoData.therapyCategory,
            intake_type: demoData.intakeType,
            missing_fields: demoData.missingFields,
            hasDraft: false,
          });
          return { success: true, data: demoData, hasDraft: false };
        }
        return { success: false, error: data.message || 'Invalid token' };
      }
    } catch (error) {
      console.error('Token validation error:', error);
      // Network error - use demo fallback if demo token
      if (isDemoToken) {
        const demoData = createDemoData();
        updateFormState({
          token,
          provider_name: demoData.providerName,
          clinic_name: demoData.clinicName,
          expires_at: demoData.expiresAt,
          therapyCategory: demoData.therapyCategory,
          intake_type: demoData.intakeType,
          missing_fields: demoData.missingFields,
          hasDraft: false,
        });
        return { success: true, data: demoData, hasDraft: false };
      }
      return { success: false, error: 'Failed to validate token' };
    }
  }, [updateFormState]);

  const submitIntake = useCallback(async (payment?: { paymentMethodId: string; setupIntentId: string }) => {
    try {
      // Base URL already includes /api from env variable
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
      
      // Re-validate the intake status before submission (skip for demo tokens)
      if (formState.token && !formState.token.startsWith('demo-') && formState.token !== 'demo-token') {
        const validateResponse = await fetch(`${baseUrl}/intakes/validate/${formState.token}`);
        const validateData = await validateResponse.json();
        
        if (!validateData.success) {
          // Intake has been cancelled, expired, or is invalid
          const errorMessage = validateData.message || 'This intake link is no longer valid.';
          return { 
            success: false, 
            error: errorMessage,
            isCancelled: errorMessage.includes('cancelled') || errorMessage.includes('expired')
          };
        }
      }
      
      const response = await fetch(`${baseUrl}/intakes/submit/${formState.token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          responses: {
            basic_info: formState.basic_info,
            medical_history: formState.medical_history,
            questionnaires: formState.questionnaires,
            lifestyle: formState.lifestyle,
            shipping_billing: formState.shipping_billing,
          },
          scores: {
            adam: Object.values(formState.questionnaires.adam).filter(v => v === true).length,
            stopBang: Object.values(formState.questionnaires.stopBang).filter(v => v === true).length,
          },
          paymentMethodId: payment?.paymentMethodId,
          setupIntentId: payment?.setupIntentId,
          appointmentSlot: formState.selectedSlot || undefined,
        }),
      });
      
      // If response is OK (200-299), consider it successful
      if (response.ok) {
        try {
          const data = await response.json();
          // Check for explicit success field, default to true if not present
          if (data.success !== false) {
            // DON'T reset form here - let the component handle it after showing success
            return { success: true, appointment: data?.data?.appointment ?? null };
          } else {
            // Check if cancelled or expired
            const isCancelled = data.message?.includes('cancelled') || data.message?.includes('expired');
            return { success: false, error: data.message || 'Failed to submit intake', isCancelled };
          }
        } catch (jsonError) {
          // If JSON parsing fails but response was OK, still consider it success
          console.log('Response OK but no JSON:', jsonError);
          // DON'T reset form here
          return { success: true };
        }
      } else {
        // Response not OK
        const data = await response.json().catch(() => ({ message: 'Failed to submit intake' }));
        const isCancelled = data.message?.includes('cancelled') || data.message?.includes('expired');
        return { success: false, error: data.message || 'Failed to submit intake', isCancelled };
      }
    } catch (error) {
      console.error('Submit error:', error);
      // For demo, simulate success
      if (formState.token === 'demo-token' || formState.token?.startsWith('demo-')) {
        // DON'T reset form here - let the component handle it
        return { success: true };
      }
      return { success: false, error: 'Failed to submit intake' };
    }
  }, [formState, resetForm]);

  // Compute targeted navigation helpers
  const targetedNav: TargetedNavigation = useMemo(() => {
    const showPayment = formState.isGuided
      && formState.paymentStatus !== 'hold_placed'
      && formState.paymentStatus !== 'captured';
    const showBooking = formState.isNimbusBookingEligible && showPayment;
    const activeSteps = computeActiveSteps(
      formState.intake_type,
      formState.missing_fields,
      showPayment,
      showBooking,
    );
    const baseStepCount = 6 + (showBooking ? 1 : 0) + (showPayment ? 1 : 0);
    const isTargeted = formState.intake_type === 'targeted' && activeSteps.length < baseStepCount;
    const totalActiveSteps = activeSteps.length;
    // activeStepConfigs must follow activeSteps order, not numeric order, so
    // Booking (8) renders after Review (6) instead of after Payment (7).
    const configByNumber = new Map(ALL_STEP_CONFIGS.map(c => [c.number, c]));
    const activeStepConfigs = activeSteps
      .map(n => configByNumber.get(n))
      .filter((c): c is typeof ALL_STEP_CONFIGS[number] => Boolean(c));

    const stepPosition = (step: number): number => {
      const idx = activeSteps.indexOf(step);
      return idx >= 0 ? idx + 1 : 1;
    };

    const nextStep = (current: number): number | null => {
      const idx = activeSteps.indexOf(current);
      return idx >= 0 && idx < activeSteps.length - 1 ? activeSteps[idx + 1] : null;
    };

    const prevStep = (current: number): number | null => {
      const idx = activeSteps.indexOf(current);
      return idx > 0 ? activeSteps[idx - 1] : null;
    };

    return { activeSteps, isTargeted, totalActiveSteps, stepPosition, nextStep, prevStep, activeStepConfigs };
  }, [
    formState.intake_type,
    formState.missing_fields,
    formState.isGuided,
    formState.paymentStatus,
    formState.isNimbusBookingEligible,
  ]);

  return (
    <IntakeContext.Provider value={{ 
      formState, 
      updateFormState, 
      resetForm, 
      validateToken, 
      submitIntake,
      saveDraft,
      loadDraft,
      clearDraft,
      targetedNav
    }}>
      {children}
    </IntakeContext.Provider>
  );
};

export const useIntakeForm = () => {
  const context = useContext(IntakeContext);
  if (context === undefined) {
    throw new Error('useIntakeForm must be used within an IntakeProvider');
  }
  return context;
};

export default IntakeContext;
